import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { useFeature } from '@treams/treams-hooks/useFeature';
import { useLocalize } from '@treams/treams-hooks/useLocalize';
import { useViewer } from '@treams/treams-hooks/useViewer';
import { CompassIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Card, ListItem, Paragraph, Pill, Sidebar, Skeleton } from '@treams/treams-ui';

import { sortByOrderThenTitle } from '../competencies/sortJobCompetenciesByOrder';
import CompetencyProgressPill from '../growth-path/components/CompetencyProgressPill';
import {
	growthPathDataMappingFromUser,
	growthPathJobPositions,
} from '../growth-path/GrowthPathConverter';
import { useCompetencyProgress } from '../growth-path/hooks/useCompetencyProgress';
import { useGrowthPathUserCompetenciesQuery } from '../growth-path/hooks/useGrowthPathScale';
import { GrowthPathJobCompetency } from '../growth-path/types';
import { useJobTitleName } from '../job/useJobTitleName';
import { useModalLink } from '../navigation/ModalNavigation';
import { Section } from './ContextSidebarSection';

function competencyPath(
	userID: string,
	viewerID: string | undefined,
	jobCompetency: GrowthPathJobCompetency,
	detailScreen: boolean,
) {
	const base = viewerID === userID ? '/growth-path' : `/users/${userID}/growth-path`;
	const role = `${base}/my-job-title/${jobCompetency.jobTitleID}`;

	if (!detailScreen) {
		return role;
	}

	return `${role}/competency/${jobCompetency.competency.id}?jobTitleID=${jobCompetency.jobTitleID}`;
}

function GrowthPathCompetencyRow(props: {
	userID: string;
	viewerID: string | undefined;
	jobCompetency: GrowthPathJobCompetency;
	jobTitleLabel?: string;
	detailScreen: boolean;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const history = useHistory();
	const progress = useCompetencyProgress(
		props.jobCompetency.appraisal ?? null,
		props.jobCompetency.competencyLevel,
	);
	const levelIndex = progress?.level ?? props.jobCompetency.competencyLevel;
	const level = props.jobCompetency.competency.levels[levelIndex];
	const levelName = level ? (localize(level.level.title).text ?? '') : '';
	const meta = props.jobTitleLabel ? `${props.jobTitleLabel} · ${levelName}` : levelName;
	const link = useModalLink(
		competencyPath(props.userID, props.viewerID, props.jobCompetency, props.detailScreen),
	);

	return (
		<ListItem
			small
			title={localize(props.jobCompetency.competency.title ?? []).text ?? ''}
			meta={meta}
			trailing={
				progress ? (
					<CompetencyProgressPill progress={progress.progress} />
				) : (
					<Pill small flexShrink={0}>
						{t('growthPath.competency.noScore')}
					</Pill>
				)
			}
			onClick={() => history.push(link)}
		/>
	);
}

function GrowthPathPeekList(props: { userID: string; isOpen: boolean }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const viewer = useViewer();
	const jobTitleName = useJobTitleName();
	const detailScreen = useFeature(graphql.FeatureName.CompetencyDetailScreen);
	const query = useGrowthPathUserCompetenciesQuery({ id: props.userID }, { skip: !props.isOpen });

	if (query.loading && !query.data) {
		return (
			<Skeleton.Repeat count={3}>
				<Sidebar.Item.Skeleton />
			</Skeleton.Repeat>
		);
	}

	if (!query.data) {
		return (
			<Paragraph pt={2} pb={4} textAlign="center">
				{t('conversation.sidebar.growthPath.empty')}
			</Paragraph>
		);
	}

	const positions = growthPathJobPositions(query.data.user);
	const jobTitleLabels = new Map(
		positions.map((position) => [position.jobTitle.id, jobTitleName(position.jobTitle)]),
	);
	const rows = sortByOrderThenTitle(
		growthPathDataMappingFromUser(query.data.user, 'all', query.scale).jobCompetencies,
		(jobCompetency) => jobCompetency.order,
		(jobCompetency) => localize(jobCompetency.competency.title ?? []).text ?? '',
	);

	if (rows.length === 0) {
		return (
			<Paragraph pt={2} pb={4} textAlign="center">
				{t('conversation.sidebar.growthPath.empty')}
			</Paragraph>
		);
	}

	return (
		<Card p={0}>
			{rows.map((jobCompetency) => (
				<GrowthPathCompetencyRow
					key={`${jobCompetency.jobTitleID ?? ''}/${jobCompetency.competency.id}`}
					userID={props.userID}
					viewerID={viewer?.id}
					jobCompetency={jobCompetency}
					jobTitleLabel={
						positions.length > 1 ? jobTitleLabels.get(jobCompetency.jobTitleID ?? '') : undefined
					}
					detailScreen={detailScreen}
				/>
			))}
		</Card>
	);
}

export default function SidebarPeekGrowthPath(props: {
	userID: string;
	viewerCanViewFeedback: boolean;
}) {
	const { t } = useTranslation();
	const hasGrowthPath = useFeature(graphql.FeatureName.GrowPath);
	const hasJobCompetenciesExplorer = useFeature(graphql.FeatureName.JobCompetenciesExplorer);
	const [isOpen, setIsOpen] = useState(false);

	if (!hasGrowthPath || !props.viewerCanViewFeedback) {
		return null;
	}

	return (
		<Section
			title={
				hasJobCompetenciesExplorer ? t('sidebar.peer.growthPath') : t('sidebar.peer.competencies')
			}
			icon={<CompassIcon size={20} />}
			isOpen={isOpen}
			onToggle={() => setIsOpen((open) => !open)}
		>
			<GrowthPathPeekList userID={props.userID} isOpen={isOpen} />
		</Section>
	);
}
